/**
 * WebhookGuide - clean, copyable setup guide for a webhook source.
 * Shows the two endpoints (/events, /signal), the signing secret (only at
 * creation), and a full curl example for each. Used by the connect success
 * screen and the source detail page.
 */

import React from 'react';
import { Box, Paper, Typography, Button, Stack, Alert, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeBlock from '../common/CodeBlock';

const copy = (t) => { try { navigator.clipboard.writeText(t); } catch (_e) { /* ignore */ } };

const label = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary',
};

const curl = (url, body) =>
  `curl -X POST ${url} \\\n`
  + `  -H 'Content-Type: application/json' \\\n`
  + `  -H 'X-Signature: <HMAC_SHA256(body, secret)>' \\\n`
  + `  -d '${body}'`;

export default function WebhookGuide({ eventsUrl, signalUrl, secret, card, eventConfig }) {
  const theme = useTheme();
  const box = card || { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.paper };

  // Build the events example from the source's mapping so it matches reality.
  // Uniform vocabulary: ``occurred_at`` (when it happened) is the canonical
  // field and is always shown; ``known_at`` (when recorded) defaults to now.
  const ev = eventConfig || {};
  const textKey = ev.text_path || 'text';
  const occurredKey = ev.occurred_at_path || ev.time_path || 'occurred_at';
  const eventObj = { [textKey]: 'Quarterly revenue beat expectations' };
  eventObj[occurredKey] = '2026-06-06T20:00:00Z';            // when it happened (occurred_at)
  const eventsBody = JSON.stringify(eventObj);

  return (
    <Paper elevation={0} sx={{ ...box, p: 2.5, pb: 10 }}>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
        Two endpoints share one signing secret. Send <strong>events</strong> (text) to <code>/events</code> and
        <strong> metrics</strong> (numbers) to <code>/signal</code>. Sign each request with{' '}
        <code>X-Signature = HMAC-SHA256(body, secret)</code>. Send{' '}
        <code>{occurredKey}</code> for when the event actually happened (the time we
        use everywhere); the recorded time is set automatically when we receive it.
      </Typography>

      {secret && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={label}>Signing secret</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Box component="code" sx={{ flex: 1, p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: 12,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              overflowX: 'auto', whiteSpace: 'nowrap' }}>{secret}</Box>
            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: 'none' }} onClick={() => copy(secret)}>Copy</Button>
          </Stack>
          <Alert severity="warning" sx={{ mt: 1, py: 0 }}>Save it now — it won't be shown again.</Alert>
        </Box>
      )}

      <Stack spacing={2}>
        <Box>
          <Typography sx={label}>Events</Typography>
          <Box sx={{ mt: 0.5 }}>
            <CodeBlock language="bash" title={`POST ${eventsUrl}`} code={curl(eventsUrl, eventsBody)} />
          </Box>
        </Box>
        <Box>
          <Typography sx={label}>Signals</Typography>
          <Box sx={{ mt: 0.5 }}>
            <CodeBlock language="bash" title={`POST ${signalUrl}`}
              code={curl(signalUrl, '{"symbol": "AAPL", "price": 191.2, "volume": 1000, "occurred_at": "2026-06-06T20:00:00Z"}')} />
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}
